import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetClubNameFilter,
  resetLeagueFilter,
  resetStarRatingFilter,
  resetTargetStarRatingFilter,
  setClubNameFilter,
  setLeagueFilter,
  setLeagues,
  setStarRatingFilter,
  setTargetStarRatingFilter,
} from '../../../redux/reducers/common';
import { getLeagues } from '../../../api/common';
import AutoComplete from '../../AutoComplete';
import { Box, Button, Divider, IconButton, InputLabel, Tooltip, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faXmark } from '@fortawesome/free-solid-svg-icons';
import { colors } from '../../../common/pallette/colors';

const ALL_STAR_OPTIONS = [
  { id: 1, name: '1 Star' },
  { id: 2, name: '2 Star' },
  { id: 3, name: '3 Star' },
  { id: 4, name: '4 Star' },
];

interface StarOption {
  id: number;
  name: string;
}

interface StarRatingSelectorProps {
  label: string;
  availableOptions: StarOption[];
  selected: StarOption[];
  onToggle: (opt: StarOption) => void;
  onReset: () => void;
}

const StarRatingSelector: React.FC<StarRatingSelectorProps> = ({
  label,
  availableOptions,
  selected,
  onToggle,
  onReset,
}) => {
  return (
    <Box sx={{ flex: '0 0 auto' }}>
      <InputLabel
        className="fs12"
        sx={{
          color: colors.black.dark,
          mb: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          textAlign: 'left',
        }}
      >
        {label}
        {selected.length > 0 && (
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '16px',
              height: '16px',
              borderRadius: '50%',
              backgroundColor: colors.blue.navy,
              color: '#fff',
              fontSize: '10px',
              fontWeight: 600,
              lineHeight: 1,
              padding: '0 3px',
              flexShrink: 0,
            }}
          >
            {selected.length}
          </Box>
        )}
      </InputLabel>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px',
          height: '34px',
          border: `1px solid ${colors.black.dark}`,
          borderRadius: '4px',
          padding: '0 6px',
          backgroundColor: '#fff',
        }}
      >
        {ALL_STAR_OPTIONS.map((opt) => {
          const isAvailable = availableOptions.some((o) => o.id === opt.id);
          const isSelected = selected.some((s) => s.id === opt.id);
          return (
            <Tooltip
              key={opt.id}
              title={isAvailable ? opt.name : `${opt.name} (unavailable)`}
              arrow
              placement="top"
            >
              <Box
                onClick={() => isAvailable && onToggle(opt)}
                sx={{
                  cursor: isAvailable ? 'pointer' : 'not-allowed',
                  opacity: isAvailable ? 1 : 0.3,
                  padding: '3px 5px',
                  borderRadius: '3px',
                  backgroundColor: isSelected ? `${colors.blue.navy}15` : 'transparent',
                  border: isSelected
                    ? `1px solid ${colors.blue.navy}50`
                    : '1px solid transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1px',
                  transition: 'background-color 0.12s, border-color 0.12s',
                  '&:hover': isAvailable
                    ? {
                        backgroundColor: isSelected
                          ? `${colors.blue.navy}20`
                          : colors.grey.light,
                      }
                    : {},
                }}
              >
                {Array.from({ length: opt.id }).map((_, i) => (
                  <FontAwesomeIcon
                    key={i}
                    icon={faStar}
                    style={{
                      fontSize: '9px',
                      color: isSelected ? colors.yellow.bright : colors.grey.dark,
                      transition: 'color 0.12s',
                    }}
                  />
                ))}
              </Box>
            </Tooltip>
          );
        })}
        <Box sx={{ ml: '2px', display: 'flex', alignItems: 'center' }}>
          <IconButton
            size="small"
            onClick={onReset}
            sx={{
              padding: '2px',
              opacity: selected.length > 0 ? 1 : 0,
              pointerEvents: selected.length > 0 ? 'auto' : 'none',
              transition: 'opacity 0.15s',
            }}
          >
            <ClearIcon sx={{ fontSize: '14px' }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

interface FiltersWrapperProps {
  globalFiltersCount?: number;
  onClearAll?: () => void;
  onClose?: () => void;
}

const FiltersWrapper: React.FC<FiltersWrapperProps> = ({ globalFiltersCount = 0, onClearAll, onClose }) => {
  const selectedClubs = useSelector((state: any) => state.common.clubNameFilter);
  const clubData = useSelector((state: any) => state.common.myClubs);

  const selectedLeagues = useSelector((state: any) => state.common.leagueFilter);
  const leagueData = useSelector((state: any) => state.common.leagues);

  const selectedStars = useSelector((state: any) => state.common.starRatingFilter);
  const selectedTargetStars = useSelector(
    (state: any) => state.common.targetStarRatingFilter
  );

  const [clubNameOptions, setClubNameOptions] = useState(clubData);
  const [leagueOptions, setLeagueOptions] = useState(leagueData);
  const [starRatingOptions, setStarRatingOptions] = useState(ALL_STAR_OPTIONS);
  const [targetRatingOptions, setTargetRatingOptions] = useState(ALL_STAR_OPTIONS);

  const dispatch = useDispatch();

  useEffect(() => {
    const leagueIds = selectedLeagues?.map((d: any) => d.id);
    const starsIds = selectedStars?.map((d: any) => d.id);
    const targetStarIds = selectedTargetStars?.map((d: any) => d.id);

    const tempClubs = clubData?.filter(
      (d: any) =>
        (leagueIds.includes(d.leagueId) || leagueIds.length === 0) &&
        (starsIds.includes(d.currentRating) || starsIds.length === 0) &&
        (targetStarIds.includes(d.targetRating) || targetStarIds.length === 0)
    );
    setClubNameOptions(tempClubs);

    const clubLeagueIds = selectedClubs?.map((d: any) => d.leagueId);
    setLeagueOptions(
      leagueData?.filter(
        (d: any) => clubLeagueIds.includes(d.id) || clubLeagueIds.length === 0
      )
    );

    const clubStarIds = selectedClubs?.map((d: any) => d.currentRating);
    setStarRatingOptions(
      ALL_STAR_OPTIONS.filter(
        (d) => clubStarIds.includes(d.id) || clubStarIds.length === 0
      )
    );

    const clubTargetStarIds = selectedClubs?.map((d: any) => d.targetRating);
    setTargetRatingOptions(
      ALL_STAR_OPTIONS.filter(
        (d) => clubTargetStarIds.includes(d.id) || clubTargetStarIds.length === 0
      )
    );
  }, [selectedClubs, selectedLeagues, selectedStars, selectedTargetStars, clubData, leagueData]);

  useEffect(() => {
    const fetchLeagues = async () => {
      const response = await getLeagues();
      return response;
    };
    fetchLeagues().then((res) => {
      dispatch(setLeagues(res));
      setLeagueOptions(res);
    });
  }, [dispatch]);

  // How many clubs will appear in the results given current filters
  const resultCount = useMemo(() => {
    if (selectedClubs.length > 0) {
      return clubNameOptions?.filter((c: any) =>
        selectedClubs.some((sc: any) => sc.id === c.id)
      ).length ?? 0;
    }
    return clubNameOptions?.length ?? 0;
  }, [selectedClubs, clubNameOptions]);

  const d: any = dispatch;

  const manageClubNameSelect = (club: any) => {
    const index = selectedClubs.findIndex((obj: any) => obj.id === club.id);
    const tempArr: any[] = [...selectedClubs];
    if (index !== -1) {
      tempArr.splice(index, 1);
    } else {
      tempArr.push(club);
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    d(setClubNameFilter(tempArr));
  };

  const manageLeagueSelect = (league: any) => {
    const index = selectedLeagues.findIndex((obj: any) => obj.id === league.id);
    const tempArr: any[] = [...selectedLeagues];
    if (index !== -1) {
      tempArr.splice(index, 1);
    } else {
      tempArr.push(league);
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    d(setLeagueFilter(tempArr));
  };

  const manageStarRatingSelect = (star: StarOption) => {
    const index = selectedStars.findIndex((obj: any) => obj.id === star.id);
    const tempArr: any[] = [...selectedStars];
    if (index !== -1) {
      tempArr.splice(index, 1);
    } else {
      tempArr.push(star);
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    d(setStarRatingFilter(tempArr));
  };

  const manageTargetStarRatingSelect = (star: StarOption) => {
    const index = selectedTargetStars.findIndex((obj: any) => obj.id === star.id);
    const tempArr: any[] = [...selectedTargetStars];
    if (index !== -1) {
      tempArr.splice(index, 1);
    } else {
      tempArr.push(star);
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    d(setTargetStarRatingFilter(tempArr));
  };

  const hasAnyFilter =
    selectedClubs.length > 0 ||
    selectedLeagues.length > 0 ||
    selectedStars.length > 0 ||
    selectedTargetStars.length > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        // Bottom-align all fields so input boxes sit on the same baseline
        // regardless of whether a label has a badge (which can vary label height)
        alignItems: 'flex-end',
      }}
    >
      {/* Club Name — chip mode */}
      <Box sx={{ minWidth: '220px', flex: '2 1 220px', maxWidth: '340px' }}>
        <AutoComplete
          options={clubNameOptions}
          selectedValues={selectedClubs}
          handleMenuClick={(d: any) => manageClubNameSelect(d)}
          label="Club Name"
          clickReset={() => dispatch(resetClubNameFilter())}
          isMultiple={true}
          showLabelBadge={true}
          showChips={true}
          onChipRemove={(club: any) => manageClubNameSelect(club)}
        />
      </Box>

      {/* League — chip mode */}
      <Box sx={{ minWidth: '180px', flex: '2 1 180px', maxWidth: '280px' }}>
        <AutoComplete
          options={leagueOptions}
          selectedValues={selectedLeagues}
          handleMenuClick={(d: any) => manageLeagueSelect(d)}
          label="League"
          clickReset={() => dispatch(resetLeagueFilter())}
          isMultiple={true}
          showLabelBadge={true}
          showChips={true}
          onChipRemove={(league: any) => manageLeagueSelect(league)}
        />
      </Box>

      {/* Star Rating — visual */}
      <StarRatingSelector
        label="Star Rating"
        availableOptions={starRatingOptions}
        selected={selectedStars}
        onToggle={manageStarRatingSelect}
        onReset={() => dispatch(resetStarRatingFilter())}
      />

      {/* Target Rating — visual */}
      <StarRatingSelector
        label="Target Rating"
        availableOptions={targetRatingOptions}
        selected={selectedTargetStars}
        onToggle={manageTargetStarRatingSelect}
        onReset={() => dispatch(resetTargetStarRatingFilter())}
      />

      {/* Actions row: result count pill + Clear all + × — pinned to right, bottom-aligned */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          ml: 'auto',
          flexShrink: 0,
          // Match the 34px input height so this row sits on the same baseline
          height: '34px',
        }}
      >
        {/* Result count — inline pill, always visible */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            backgroundColor: hasAnyFilter ? `${colors.blue.navy}08` : 'transparent',
            border: `1px solid ${hasAnyFilter ? `${colors.blue.navy}25` : 'transparent'}`,
            borderRadius: '20px',
            padding: '2px 10px 2px 8px',
            transition: 'all 0.2s',
            mr: '4px',
          }}
        >
          <Box
            sx={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: hasAnyFilter ? colors.blue.navy : colors.grey.dark,
              flexShrink: 0,
              transition: 'background-color 0.2s',
            }}
          />
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: hasAnyFilter ? 600 : 400,
              color: hasAnyFilter ? colors.blue.navy : colors.grey.dark,
              whiteSpace: 'nowrap',
              transition: 'color 0.2s',
            }}
          >
            {resultCount} {resultCount === 1 ? 'club' : 'clubs'}
          </Typography>
        </Box>

        {globalFiltersCount > 0 && onClearAll && (
          <>
            <Divider orientation="vertical" flexItem sx={{ height: '20px', alignSelf: 'center' }} />
            <Button
              variant="outlined"
              size="small"
              onClick={onClearAll}
              sx={{
                fontSize: '11px',
                color: colors.blue.navy,
                borderColor: `${colors.blue.navy}50`,
                textTransform: 'none',
                fontWeight: 500,
                padding: '3px 10px',
                borderRadius: '4px',
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: colors.blue.navy,
                  backgroundColor: `${colors.blue.navy}08`,
                },
              }}
            >
              Clear all
            </Button>
          </>
        )}

        {onClose && (
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: colors.grey.dark,
              border: `1px solid ${colors.grey.light}`,
              borderRadius: '4px',
              padding: '4px 6px',
              '&:hover': { backgroundColor: colors.grey.light, color: colors.black.dark },
            }}
          >
            <FontAwesomeIcon icon={faXmark} style={{ fontSize: '12px' }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default FiltersWrapper;
